// CONVERT DESK BALANCE v2 — one of the desk's own accounts, some of what it holds into another asset, through whoever
// changed the money (UI rebuild batch 3; Astra's convert-desk).
//
// THE STATE MACHINE IS screens/TreasurySwapForm.tsx's, COPIED VERBATIM (its module helpers too — they are not exported):
// the treasury read and its key, `unknown`, the venue / bank self-conversion, the To asset and the same-coin-other-chain
// stop, where it lands (canLand, the defaults), the party search and the remembered parties, the own-account row for a
// venue, the shared calculator, the band check, the venue fee, the fills read (by `venue_account`, never the brand word)
// and its per-day presets with their over / not-booked checks, the direction rule, `ready`, the deal id minted once and
// held across a retry, the confirmations keyed by the guard that refused (repeat, money, new_rail), the new-account door,
// the posted / already-posted lines and the fee to record, ServerLine. Only the drawing changed:
//
//   Astra's order — Money changer | From account, To asset | To account, From amount | To amount, Rate — every one
//   visible from the start (the ones that need the To asset wait, disabled); the venue's fills and the fee behind a tap;
//   "Before confirming" names the money changer, both accounts and the effect; "Review conversion", then the confirm
//   naming the figure. The explanation is "About this job".
import { useState, useMemo, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type Direction, type CexFillSummary, type TreasuryRow } from '../../../lib/api';
import { tg } from '../../../lib/telegram';
import { ASSETS, DEFAULT_RAILS, isFiat, kindsForCategory, derivePaymentMethod } from '../../../lib/workflow';
import { amountProblem, rateProblem, optionalAmountProblem } from '../../../lib/amount';
import { ApiError, isSentinel, nothingLanded } from '../../../lib/errors';
import { BALANCES_UNREAD, balancesUnread } from '../../../lib/balances-read';
import { useTradeMath } from '../../../hooks/useTradeMath';
import { bandWords } from '../../../lib/trade-math';
import { AsOfChips } from '../../AsOfChips';
import { RailPicker, type RailValue } from '../../RailPicker';
import { Combobox, type ComboboxOption } from '../../Combobox';
import { rosterCut } from '../../../lib/roster-cut';
import { Field } from '../../Field';
import { ErrorLine, WarnLine } from '../../ErrorLine';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../../RepeatPanel';
import type { Desk } from '../../DeskSwitch';
import { EmptyState, Fact, Panel, Pending, ReadFailed, ServerLine, StateLine, TAP } from '../kit';
import { sayFirst } from '../../kit';
import { partyName, partySubtitle, partyBoxWords, isStale, needsNameReview, railName, sameAsset, foldAsset, brandCase } from '../../../lib/naming';
import { ledgerText } from '../../../lib/ledger-scale';
import { fillsFrom, prefillOf, widened } from '../../../lib/convert-prefill';
import {
  displayAsset, fmtAmount, fmtQuantity, fmtDay, fmtRate, fmtRateAsGiven, fmtRateExact, dubaiDate,
  dubaiWallToIso, isoToDubaiWall, backdateProblem,
} from '../../../lib/format';
import { Fold, FormCard, FormFrame, Review, ReviewThenConfirm } from './layout';
import { Receipt, ReceiptAmount, postedAsOf } from '../Receipt';
import { RateRow } from '../record/BalanceRows';

/** Map workflow's payment_method ('bank-transfer') to the BFF's delivery_method ('bank_transfer') — NewTrade's table. */
const PM_TO_DM: Record<string, 'cash' | 'bank_transfer' | 'crypto' | undefined> = {
  cash: 'cash', 'bank-transfer': 'bank_transfer', crypto: 'crypto', other: undefined,
};

/** Can this rail take the To asset? It holds that exact code, or the books' bare code for the same coin. */
const canLand = (rowAsset: string | undefined, to: string): boolean =>
  !!rowAsset && (rowAsset === to || (rowAsset === foldAsset(rowAsset) && sameAsset(rowAsset, to)));   // dialect-ok: exact chain code on purpose — the fold alone would land TRC coins on an ERC wallet

/** The exchange's BRAND NAME, for DISPLAY only (TreasurySwapForm.tsx: not the account — see `venueAccount`). */
const venueOfRail = (slug: string): string =>
  slug.startsWith('exchange.') ? (slug.split('.')[1] ?? '').replace(/-india$/, '') : '';

/** How far back the fills panel reads: a week — a day's fills booked on a later day are still offered (a booked day
 *  leaves the list by itself: the read's after_conversion). */
const FILLS_DAYS = 7;

/** A day's rate as the venue's figures give it: its stored places, the whole part grouped — never rounded (rule 10; the
 *  table read 71,250.123456 as "71,250.1235", 4 Nov 2026). */
const fillRate = (rate: number | null): string => fmtRateAsGiven(fmtRateExact(rate));

/** A plain decimal for the calculator, as the books will hold it (lib/ledger-scale.ts). */
const plain = ledgerText;

/** The fills table's columns from lg: day · in · out · rate · Use (on the phone each figure is a labelled row instead). */
// The fee is not a column: in a 600 px card six columns ran a unit into its neighbour (1 Nov 2026 audit); it is a labelled
// row under its day when the venue took one. Every column is as wide as its widest cell, never narrower: fixed tracks ran
// "298,139.27 USDC" into "77,189.854" and cut the unit (his screenshot, 4 Nov 2026). The head and each row share the
// table's tracks (subgrid), so the columns still line up; In and Out share what is left.
const FILL_COLS = 'lg:grid lg:grid-cols-[max-content_minmax(max-content,1fr)_minmax(max-content,1fr)_max-content_max-content] lg:gap-x-4';
const FILL_ROW = 'lg:col-span-full lg:grid lg:grid-cols-subgrid';

/** A venue day whose pair is not this form's yet: the day, what it bought or sold and for what — each in its own place —
 *  and the one tap that makes its coin the To (1 Nov 2026 audit: it was one sentence with a link in it). */
function OtherFill({ g, base, quote, pairFrom, toCode, fromName, disabled, onTo }: {
  g: CexFillSummary; base: string; quote: string; pairFrom: string; toCode: string | null; fromName: string; disabled: boolean; onTo: (code: string) => void;
}) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-stroke-soft py-2 text-body last:border-b-0">
      <span className="whitespace-nowrap font-mono tabular text-meta">{fmtDay(g.first_at ?? g.day)}</span>
      <span className="min-w-0 space-y-0.5">
        <span className="flex items-baseline justify-between gap-3"><span className="text-meta text-tg-hint">{g.side === 'buy' ? 'Bought' : 'Sold'}</span><ReceiptAmount value={g.amount} asset={base} /></span>
        <span className="flex items-baseline justify-between gap-3"><span className="text-meta text-tg-hint">{g.side === 'buy' ? 'With' : 'For'}</span><ReceiptAmount value={g.cost} asset={quote} /></span>
      </span>
      {toCode
        ? <button type="button" className="btn-secondary min-h-[44px] px-3 text-body lg:min-h-[40px]" disabled={disabled} onClick={() => onTo(toCode)}>To {displayAsset(toCode)}</button>
        : <span className="max-w-[9rem] text-right text-meta text-tg-hint">From {fromName} · {displayAsset(pairFrom)}</span>}
    </div>
  );
}

type PairFills = { g: CexFillSummary; toAmount: number; fromAmount: number; fee: number; feeAsset: string | null };

/** What the venue's fills say for THIS pair, one entry per Dubai day, earliest first (TreasurySwapForm.tsx). */
function fillsForPair(summary: CexFillSummary[], from: string, to: string): PairFills[] {
  const f = foldAsset(from), t = foldAsset(to);
  return summary
    .filter((s) => s.amount > 0 && ((s.symbol === `${t}/${f}` && s.side === 'buy') || (s.symbol === `${f}/${t}` && s.side === 'sell')))
    .sort((a, b) => (a.day ?? a.first_at ?? '').localeCompare(b.day ?? b.first_at ?? ''))
    .map((g) => {
      const buy = g.side === 'buy';
      const [feeAsset, fee] = Object.entries(g.fees).find(([, v]) => v !== 0) ?? [null, 0];
      return { g, toAmount: buy ? g.amount : g.cost, fromAmount: buy ? g.cost : g.amount, fee, feeAsset };
    });
}

/** The From account as compact context (Astra: "read-only context is compact text, not another large input"): the
 *  account and its asset, its balance on the books muted beside it, and the way back to the list. */
export function FromAccount({ row, slug, asset, change }: { row: TreasuryRow | null | undefined; slug: string; asset: string; change?: ReactNode }) {
  return (
    <Field label="From account">
      <div className="flex min-h-[44px] items-center justify-between gap-3 lg:min-h-[40px]">
        <span className="min-w-0">
          <span className="block break-words text-body font-medium">{railName(row ?? { slug })} · {displayAsset(row?.asset ?? asset)}</span>
          {row && <span className="block font-mono tabular text-meta text-tg-hint">{fmtAmount(row.balance, row.asset)}</span>}
        </span>
        {change}
      </div>
    </Field>
  );
}

/** "About this job" for a desk account's conversion — behind a tap (Swap.tsx's help, the desk half). */
function DeskAbout() {
  return (
    <>
      <p>
        A desk account converts through whoever changed the money — a settled trade with that merchant: they take the From
        asset off the account and give the To asset onto the account it lands on. The same coin on another chain is two
        moves on Move money, not a conversion.
      </p>
      <p>
        On an exchange, a day&rsquo;s fills button takes the venue&rsquo;s fee inside the figures; to record the fee as a cost
        on its own, enter it under More → Venue fee.
      </p>
    </>
  );
}

export function DeskConvertForm({ entity, from, change }: { entity: Desk; from: RailValue; change?: ReactNode }) {
  const nav = useNavigate();

  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];
  const fromRow = useMemo(
    () => rows.find((r) => r.slug === from.slug && sameAsset(r.asset, from.asset)),
    [rows, from.slug, from.asset],
  );
  const balance = fromRow?.balance ?? 0;
  // A REFRESH THAT FAILED keeps the last good read beside the error: no claim drawn from it, nothing posted off it.
  const unknown = treasury.isError;
  const fromName = railName(fromRow ?? { slug: from.slug });

  const venue = venueOfRail(from.slug);
  const isExchange = venue !== '';
  const isBank = from.slug.startsWith('bank.');
  const selfConverts = isExchange || isBank;
  const venueName = isExchange ? brandCase(venue) : fromName;

  // OPENED FROM AN UNBOOKED LINE on Convert's home (lib/convert-prefill.ts): its To asset, and the day to preset.
  const [search] = useSearchParams();
  const [pre] = useState(() => prefillOf(search, from.asset, ASSETS));
  const [to, setTo] = useState(pre.to);
  const [landsOn, setLandsOn] = useState<RailValue>({ slug: '', asset: '' });
  const [partySlug, setPartySlug] = useState('');
  const [notes, setNotes] = useState('');
  const [venueFee, setVenueFee] = useState('');
  const [venueFeeAsset, setVenueFeeAsset] = useState('');
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // The same coin on another chain is not a conversion — two moves on Move money.
  const excludeFrom = (a: string) => a === from.asset;   // dialect-ok: exact chain code on purpose — the same coin on another chain must stay pickable
  const chainSwap = !!to && to !== from.asset && sameAsset(to, from.asset);   // dialect-ok: same fold, different chain code
  const pairKnown = !!to && !chainSwap;

  // WHERE THE TO ASSET LANDS (TreasurySwapForm.tsx's rule and defaults).
  const landRows = useMemo(() => (pairKnown ? rows.filter((r) => canLand(r.asset, to)) : []), [rows, to, pairKnown]);
  useEffect(() => {
    if (!pairKnown) { setLandsOn({ slug: '', asset: '' }); return; }
    if (selfConverts) { setLandsOn({ slug: from.slug, asset: to }); return; }
    const same = rows.find((r) => r.slug === from.slug && canLand(r.asset, to));
    if (same)                                            { setLandsOn({ slug: same.slug!, asset: same.asset! }); return; }
    if (entity === 'dubaiotc' && sameAsset(to, DEFAULT_RAILS.cash.asset)) { setLandsOn({ ...DEFAULT_RAILS.cash });   return; }
    if (entity === 'dubaiotc' && to === DEFAULT_RAILS.hotTrc.asset)       { setLandsOn({ ...DEFAULT_RAILS.hotTrc }); return; }   // dialect-ok: exact chain code — one rail per chain
    setLandsOn({ slug: '', asset: '' });
  }, [to, pairKnown]);   // eslint-disable-line react-hooks/exhaustive-deps

  // WHO CONVERTED IT — Book trade's party search, same key.
  const parties = useQuery({
    queryKey: ['parties', entity, partySlug.trim()],
    queryFn:  () => api.listParties({ entity, q: partySlug.trim() || undefined, with_balances: true }),
    enabled:  !selfConverts,
  });
  const boxesRead = !balancesUnread(parties.data);
  const partyOptions: ComboboxOption[] = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account')
      .slice(0, 20)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: [partySubtitle(p, { compact: true }), boxesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, boxesRead]);
  const knownParties = useRef<Map<string, string>>(new Map());
  const partyPicked = useMemo(() => {
    for (const p of parties.data?.parties ?? []) if (p.party_type !== 'own_account') knownParties.current.set(p.slug, partyName(p));
    return !!partySlug && knownParties.current.has(partySlug);
  }, [parties.data, partySlug]);

  // ON AN EXCHANGE (or a bank) the party is the account's own-account row.
  const ownAccounts = useQuery({
    queryKey: ['parties', 'own-accounts', entity],
    queryFn:  () => api.listParties({ entity, type: 'own_account', pageSize: 500 }),
    enabled:  selfConverts,
  });
  const venueParty = useMemo(
    () => (selfConverts ? (ownAccounts.data?.parties ?? []).find((p) => p.treasury_slug === from.slug) ?? null : null),
    [selfConverts, ownAccounts.data, from.slug],
  );
  const partyExists = selfConverts ? !!venueParty : partyPicked;
  const partyLabel = selfConverts ? venueName : (knownParties.current.get(partySlug) ?? partySlug);
  const partyForPost = selfConverts ? (venueParty?.slug ?? '') : partySlug;

  // THE THREE BOXES — the shared calculator.
  const math = useTradeMath(from.asset, to);
  const fromText = math.amountOf(from.asset);
  const toText   = to ? math.amountOf(to) : '';
  const fromN = Number(fromText) || 0;
  const toN   = Number(toText)   || 0;
  const rateN = Number(math.rate) || 0;
  const fromWords = amountProblem(fromText);
  const toWords   = amountProblem(toText);
  const rateProblemWords = rateProblem(math.rate);
  const over = !unknown && fromN > balance ? fromN - balance : 0;
  const fromAuto = math.isDerived(from.asset);
  const toAuto   = !!to && math.isDerived(to);
  const band = useMemo(() => {
    if (!pairKnown || rateProblemWords) return null;
    const w = bandWords(math.base, math.quote, rateN);
    return w;
  }, [pairKnown, rateN, rateProblemWords, math.base, math.quote]);
  const bandSaid = band ? sayFirst(band) : null;
  const bandDerived = math.derived === 'rate';

  // THE VENUE FEE (exchange only).
  const feeWords = optionalAmountProblem(venueFee);
  const feeN = Number(venueFee) || 0;
  const feeEntered = isExchange && venueFee.trim() !== '' && !feeWords && feeN > 0;

  // THE ACCOUNT THE FILLS QUERY ASKS FOR — the BFF's resolved `venue_account`, never the brand word.
  const venueAccount = fromRow?.venue_account;
  const fills = useQuery({
    queryKey: ['cex-fills', 'convert', entity, venueAccount?.venue, venueAccount?.label, FILLS_DAYS, pre.day],
    queryFn:  () => api.cexFills({
      entity, venue: venueAccount!.venue, label: venueAccount!.label,
      from: fillsFrom(FILLS_DAYS, pre.day), limit: 1, afterConversion: true,
    }),
    enabled:  isExchange && !!venueAccount,
  });
  const pairFills = useMemo(
    () => (isExchange && pairKnown && fills.data ? fillsForPair(fills.data.summary, from.asset, to) : []),
    [isExchange, pairKnown, fills.data, from.asset, to],
  );
  const otherFills = useMemo(() => {
    if (!isExchange || !fills.data) return [];
    return fills.data.summary.filter((g) => g.amount > 0).map((g) => {
      const [base = '', quote = ''] = g.symbol.split('/');
      const pairFrom = g.side === 'buy' ? quote : base;
      const pairTo   = g.side === 'buy' ? base  : quote;
      const pickable = ASSETS.find((a) => sameAsset(a, pairTo)) ?? null;
      const toCode = sameAsset(pairFrom, from.asset) && balance > 0 ? pickable : null;
      return { g, base, quote, pairFrom, toCode };
    });
  }, [isExchange, fills.data, from.asset, balance]);
  const [usedFills, setUsedFills] = useState<PairFills | null>(null);
  const feeAssetSaid = (usedFills ?? pairFills[0])?.feeAsset ?? null;
  const feeAssetPicked = venueFeeAsset
    || (feeAssetSaid ? (sameAsset(feeAssetSaid, from.asset) ? from.asset : to) : to);
  const fillsCheck = (p: PairFills): { preset: number; over: number; unbooked: boolean } => {
    const feeInFrom = !!p.feeAsset && sameAsset(p.feeAsset, from.asset);
    const preset = p.fromAmount + (feeInFrom && !feeEntered ? p.fee : 0);
    const over = !unknown && preset > balance ? preset - balance : 0;
    return { preset, over, unbooked: over > 0 && (balance <= 0 || over > preset * 0.01) };
  };
  useEffect(() => { setUsedFills(null); }, [to, from.slug, from.asset]);
  const applyFills = useCallback((p: PairFills, beforeFee: boolean) => {
    const feeInFrom = !!p.feeAsset && sameAsset(p.feeAsset, from.asset);
    const feeInTo   = !!p.feeAsset && sameAsset(p.feeAsset, to);
    const fromAmt = beforeFee || !feeInFrom ? p.fromAmount : p.fromAmount + p.fee;
    const toAmt   = beforeFee || !feeInTo   ? p.toAmount   : p.toAmount - p.fee;
    math.preset({ asset: from.asset }, plain(fromAmt, from.asset));
    math.preset({ asset: to }, plain(toAmt, to));
    if (usedFills?.g.day !== p.g.day && p.g.first_at) setWhen(isoToDubaiWall(p.g.first_at));
    setUsedFills(p);
  }, [math, from.asset, to, usedFills?.g.day]);
  // The opened day's fills, preset once when they arrive (a day with none presets nothing).
  const preDay = useRef(pre.day);
  useEffect(() => {
    const p = preDay.current ? pairFills.find((x) => x.g.day === preDay.current) : undefined;
    if (p) { preDay.current = null; applyFills(p, feeEntered); }
  }, [pairFills, applyFills, feeEntered]);
  const fillsSpan = widened(FILLS_DAYS, pre.day) ? `since ${fmtDay(pre.day!)}` : `last ${FILLS_DAYS} days`;
  useEffect(() => {
    if (usedFills) applyFills(usedFills, feeEntered);
  }, [feeEntered]);   // eslint-disable-line react-hooks/exhaustive-deps

  // THE SIDES: a currency for a coin is the one shape the desk BUYS; every other pair the desk sells what it gives.
  const direction: Direction = isFiat(from.asset) && !isFiat(to) ? 'customer_sell' : 'customer_buy';
  const ready = !unknown && pairKnown && !!landsOn.slug && sameAsset(landsOn.asset, to) && partyExists
    && fromN > 0 && toN > 0 && rateN > 0 && !fromWords && !toWords && !rateProblemWords && !whenWords && !feeWords;
  // Why Review is off, in a few words. A typed figure that is wrong, the date and the fee say their own words on the form.
  // Every reason the confirm can be off has words — a read that failed included (Astra, 1 Nov 2026: no dead button).
  const missing = unknown ? 'Accounts not read — try again.'
    : !to ? 'Pick what it converts to.'
    : !partyExists ? (parties.isError ? 'Parties not read — try again.' : selfConverts ? 'Pick the venue.' : 'Pick a party.')
    : !(fromN > 0) ? 'Enter the amount.'
    : !(toN > 0) && !(rateN > 0) ? 'Enter the rate or what it converts to.'
    : !landsOn.slug || !sameAsset(landsOn.asset, to) ? (ownAccounts.isError || treasury.isError ? 'Accounts not read — try again.' : 'Pick where it lands.')
    : fromWords || toWords || rateProblemWords ? 'Check the figures.'
    : whenWords ? 'Check the date.'
    : feeWords ? 'Check the fee.'
    : null;

  // ── submit ─────────────────────────────────────────────────────────────── (TreasurySwapForm's, verbatim)
  const dealIdRef = useRef<string | null>(null);
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const [posted, setPosted] = useState<{
    at: number; dealId: string; deduped: boolean; asOf: string | null;
    from: string; to: string; fromRail: string; toRail: string; toSlug: string;
    fromAmount: number; toAmount: number; fromAfter: number; toAfter: number;
    fee: { amount: number; asset: string; slug: string; venue: string } | null;
  } | null>(null);

  const post = async () => {
    if (!ready) throw new Error('nothing to convert');
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    const method = derivePaymentMethod(landsOn.slug);
    const landRow = rows.find((r) => r.slug === landsOn.slug && sameAsset(r.asset, landsOn.asset));
    const feeNote = feeEntered ? `venue fee ${plain(feeN, feeAssetPicked)} ${foldAsset(feeAssetPicked)}` : '';
    const data = await guard(() => api.bookTrade({
      entity,
      direction,
      party_slug: partyForPost,
      treasury_take_slug: landsOn.slug,
      treasury_give_slug: from.slug,
      delivery_method: method ? PM_TO_DM[method] : undefined,
      payment_method:  method,
      amount_take: { asset: landsOn.asset, amount: toN },
      amount_give: { asset: from.asset,    amount: fromN },
      rate:    math.rate.trim(),
      deal_id: dealId,
      notes:   [isExchange ? 'venue conversion' : isBank ? 'bank conversion' : 'desk conversion', feeNote, notes.trim()].filter(Boolean).join(' · '),
      effective_at:              whenIso || undefined,
      fills_from:                isExchange && usedFills?.g.first_at ? usedFills.g.first_at : undefined,
      fills_until:               isExchange && usedFills?.g.last_at  ? usedFills.g.last_at  : undefined,
      confirm_repeat:            confirmedRef.current.has('repeat') || undefined,
      confirm_money_moved_again: confirmedRef.current.has('money')  || undefined,
      allow_new_rail_asset:      confirmedRef.current.has('new_rail') || undefined,
    }));
    return {
      data, dealId, asOf: whenIso || null,
      from: from.asset, to: landsOn.asset, fromRail: fromName, toRail: railName(landRow ?? { slug: landsOn.slug }), toSlug: landsOn.slug,
      fromAmount: fromN, toAmount: toN,
      fromAfter: balance - fromN, toAfter: (landRow?.balance ?? 0) + toN,
      fee: feeEntered ? { amount: feeN, asset: feeAssetPicked, slug: from.slug, venue: venueName } : null,
    };
  };
  const submit = useMutation({
    mutationFn: post,
    onSuccess: ({ data, dealId, ...rest }) => {
      clearTwin(); confirmedRef.current.clear();
      dealIdRef.current = null;
      setPosted({ at: Date.now(), dealId: data.deal_id ?? dealId, deduped: !!data.deduped, ...rest });
      setTo(''); math.reset(); setWhen(''); setNotes(''); setVenueFee(''); setVenueFeeAsset(''); setUsedFills(null);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; confirmedRef.current.clear(); clearTwin(); }
  };
  const submitError: unknown = submit.error;
  const newRail = submit.isError && submitError instanceof ApiError && submitError.code === 'new_rail_asset'
    ? (() => {
        const body = submitError.body as { asset?: unknown; holds?: unknown };
        const asset = String(body.asset ?? '');
        const onFrom = sameAsset(asset, from.asset) && !sameAsset(asset, landsOn.asset);
        const slug = onFrom ? from.slug : landsOn.slug;
        return { asset: asset || landsOn.asset, slug, holds: Array.isArray(body.holds) ? body.holds.map(String) : [] };
      })()
    : null;

  // REVIEW, THEN CONFIRM: any change to what would post sends the form back to "Review conversion".
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); }, [to, landsOn.slug, landsOn.asset, partySlug, fromText, toText, math.rate, when, venueFee, venueFeeAsset]);

  const fromLine = <FromAccount row={fromRow} slug={from.slug} asset={from.asset} change={change} />;

  // THE SCREEN IS REPLACED ONLY WHEN THERE IS NOTHING TO SHOW (TreasurySwapForm.tsx's rule).
  if (treasury.isError && (!treasury.data || (balance === 0 && !posted))) {
    return <FormCard>{fromLine}<ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} tone="bad" /></FormCard>;
  }
  if (treasury.isPending) return <Pending />;
  if (balance === 0 && !posted) {
    if (isExchange) {
      const venueReading = typeof fromRow?.venue_balance === 'number' ? fromRow.venue_balance : null;
      return (
        <FormCard>
          {fromLine}
          <StateLine tone="warn" action={<Link to="/chain" className={TAP}>Book the money that moved in →</Link>}>
            {venueReading !== null ? `The venue shows ${fmtQuantity(venueReading, from.asset)} ${displayAsset(from.asset)} — nothing booked.` : 'Nothing booked on this account.'}
          </StateLine>
        </FormCard>
      );
    }
    return <FormCard>{fromLine}<EmptyState>Nothing to convert — no {displayAsset(from.asset)} held.</EmptyState></FormCard>;
  }

  const treasuryHref = `/treasury${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  const feeHref = posted?.fee
    ? `/record/expense?rail=${encodeURIComponent(posted.fee.slug)}&railAsset=${encodeURIComponent(posted.fee.asset)}`
      + `&amount=${encodeURIComponent(plain(posted.fee.amount, posted.fee.asset))}&category=other`
      + `&note=${encodeURIComponent(`${posted.fee.venue} fee · ${posted.dealId}`)}`
    : '';
  const asOfWords = whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : '';
  const landName = railName(rows.find((r) => r.slug === landsOn.slug && sameAsset(r.asset, landsOn.asset)) ?? { slug: landsOn.slug });

  // THE READ-BACK: who converts it, what leaves which account, what lands where, at what rate — before the confirm.
  const readBack = pairKnown && fromN > 0 && toN > 0 ? (
    <div>
      <Fact label="Money changer">{selfConverts ? `${venueName} itself` : partyExists ? partyLabel : '—'}</Fact>
      {/* ONE FACT PER ROW (his "check everything where it applies", 1 Nov 2026): each figure apart from its account, the
          rate and its inverse on their own lines, the figures by rule 10 as the fills list shows them. */}
      <Fact label="From"><ReceiptAmount value={fromN} asset={from.asset} /></Fact>
      <Fact label="From account">{fromName}</Fact>
      <Fact label="To"><ReceiptAmount value={toN} asset={to} /></Fact>
      <Fact label="To account">{landsOn.slug ? landName : '—'}</Fact>
      {rateN > 0 && !rateProblemWords && (
        <div className="space-y-1 py-2">
          <RateRow label="Rate" base={math.base} quote={math.quote} rate={rateN} />
          <RateRow label="Inverse" base={math.quote} quote={math.base} rate={1 / rateN} quiet approx />
        </div>
      )}
      {feeEntered && <Fact label="Venue fee"><ReceiptAmount value={feeN} asset={feeAssetPicked} /></Fact>}
      {feeEntered && <p className="pt-1 text-meta text-tg-hint">The fee is booked on its own.</p>}
      {asOfWords && <Fact label="As of">{fmtDay(whenIso)}</Fact>}
      {/* What the posting carries besides the figures (Astra, 1 Nov 2026): the venue's fills it was taken from, the note. */}
      {isExchange && usedFills && <Fact label="Fills used">{fmtDay(usedFills.g.first_at ?? usedFills.g.day)}</Fact>}
      {notes.trim() !== '' && <Fact label="Notes">{notes.trim()}</Fact>}
    </div>
  ) : null;


  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what was CONVERTED and on which
     accounts, from what posted; the venue's fee to record, when one was taken, stays as its own line. */
  if (submit.isSuccess && posted) {
    return (
      <div className="lg:max-w-[600px]">
        <FormCard>
          <Receipt
            title="Converted"
            deduped={posted.deduped}
            already="Already booked"
            movements="/trades"
            movementsLabel="Trades"
            reference={posted.dealId}
            facts={[
              { label: 'Converted', value: <ReceiptAmount value={posted.fromAmount} asset={posted.from} /> },
              { label: 'Into', value: <ReceiptAmount value={posted.toAmount} asset={posted.to} /> },
              { label: 'From account', value: posted.fromRail },
              { label: 'To account', value: posted.toRail },
              { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
            ]}
            extra={posted.fee && (
              <StateLine tone="warn" action={<Link to={feeHref} className={TAP}>Record the fee →</Link>}>
                Fee to record: {fmtQuantity(posted.fee.amount, posted.fee.asset)}&nbsp;{displayAsset(posted.fee.asset)}.
              </StateLine>
            )}
            another={{ label: 'Convert another', onClick: () => submit.reset() }}
            links={<Link to={treasuryHref} className={TAP}>Wallets →</Link>}
          />
        </FormCard>
      </div>
    );
  }

  return (
    <FormFrame
      reviewReady={!!readBack}
      reviewing={reviewing}
      notices={(treasury.isError || parties.isError || ownAccounts.isError) ? (
        <div className="space-y-2">
          {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} />}
          {parties.isError && <ReadFailed what="the parties" error={parties.error} onRetry={() => void parties.refetch()} />}
          {ownAccounts.isError && <ReadFailed what="the desk accounts" error={ownAccounts.error} onRetry={() => void ownAccounts.refetch()} />}
        </div>
      ) : null}
      warnings={(bandSaid || (!fromWords && over > 0)) ? (
        <div className="space-y-2">
          {!fromWords && over > 0 && <WarnLine>Over the balance by {fmtAmount(over, from.asset)}.</WarnLine>}
          {bandSaid && (
            <StateLine tone="warn" more={bandSaid.rest || undefined}>
              {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
            </StateLine>
          )}
        </div>
      ) : null}
      review={<Review desk={entity} readAt={treasury.dataUpdatedAt} help={<DeskAbout />}>{reviewing ? readBack : null}</Review>}
      footer={!chainSwap && (
        <ReviewThenConfirm
          reviewing={reviewing}
          setReviewing={setReviewing}
          disabled={submit.isPending || !ready}
          why={missing}
          pending={submit.isPending}
          confirmLabel={`Convert ${fmtAmount(fromN, from.asset)} ${displayAsset(from.asset)}${to ? ` to ${displayAsset(to)}` : ''}${asOfWords}`}
          onConfirm={() => submit.mutate()}
        />
      )}
      outcome={(
        <>
          {submit.isError && !isSentinel(submit.error) && !newRail && <ServerLine error={submit.error} />}
          {newRail && (
            <div className="rounded-lg bg-warn-bg p-3 space-y-2">
              <StateLine
                tone="warn"
                more={<>
                  {newRail.holds.length
                    ? `It holds ${newRail.holds.map(displayAsset).join(' / ')} today. `
                    : 'It has never been posted to. '}
                  Yes opens {displayAsset(newRail.asset)} on this account and books the conversion there — check the asset and the account first.
                </>}
              >
                {railName(rows.find((r) => r.slug === newRail.slug) ?? { slug: newRail.slug })} has not held {displayAsset(newRail.asset)} before.
              </StateLine>
              <button
                type="button"
                className="btn-secondary text-body w-full lg:w-auto"
                disabled={submit.isPending || !!whenWords || unknown}
                onClick={() => { confirmedRef.current.add('new_rail'); submit.mutate(); }}
              >
                Yes — open {displayAsset(newRail.asset)} on {railName(rows.find((r) => r.slug === newRail.slug) ?? { slug: newRail.slug })} and book
              </button>
            </div>
          )}
          {repeat && (
            <RepeatPanel
              twin={repeat}
              pending={submit.isPending || !!whenWords || unknown}
              onAlreadyBooked={() => { clearTwin(); nav(-1); }}
              onPostAnyway={() => { confirmedRef.current.add(repeat.kind); submit.mutate(); }}
            />
          )}
        </>
      )}
    >
      {/* THE MONEY CHANGER and THE FROM ACCOUNT — side by side from lg. */}
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <Field label="Money changer">
          {selfConverts ? (
            // The venue (or the bank) converted the desk's own money: it is the party, and the To asset stays on it.
            <>
              <p className="flex min-h-[44px] items-center text-body lg:min-h-[40px]">{venueName} itself</p>
              {ownAccounts.isError && <ReadFailed what="the desk accounts" error={ownAccounts.error} onRetry={() => void ownAccounts.refetch()} className="mt-1" />}
              {ownAccounts.isSuccess && !venueParty && (
                <StateLine tone="bad" className="mt-1" action={<Link to="/parties/new" className={TAP}>Parties → New →</Link>} more="Open it from Parties → New → Own account.">
                  {venueName} has no own account on this desk.
                </StateLine>
              )}
            </>
          ) : (
            <>
              <Combobox
                value={partySlug}
                onChange={setPartySlug}
                options={partyOptions}
                more={rosterCut(parties.data)}
                placeholder="Search party"
                loading={parties.isFetching || parties.fetchStatus === 'paused'}
                readFailed={parties.isError
                  ? <ReadFailed what="the parties" error={parties.error} onRetry={() => void parties.refetch()} />
                  : boxesRead ? undefined
                  : <ReadFailed what="the balances" error={BALANCES_UNREAD} onRetry={() => void parties.refetch()} />}
              />
              {!parties.isError && partySlug.trim() && !partyExists && !parties.isFetching && (
                <p className="text-meta text-tg-hint mt-1">Pick a party from the list.</p>
              )}
            </>
          )}
        </Field>
        {fromLine}
      </div>

      {/* TO: the asset, then the account it lands on — the account list depends on the asset (RailPicker's filter). */}
      <div className="grid gap-4 lg:grid-cols-[160px_minmax(0,1fr)] lg:items-start">
        <Field label="To asset">
          <select name="to_asset" className="select w-full" value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Pick</option>
            {ASSETS.filter((a) => !excludeFrom(a)).map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
          </select>
          {chainSwap && (
            <p className="text-body lg:text-meta mt-1">
              Same coin on another chain — <Link to="/treasury/rebalance" className="btn-link text-meta">use Move money →</Link>
            </p>
          )}
        </Field>
        {!chainSwap && (
          <Field label="To account">
            {!pairKnown ? (
              <select className="select w-full" disabled><option>Pick the asset first</option></select>
            ) : selfConverts ? (
              <p className="flex min-h-[44px] items-center text-body lg:min-h-[40px]">{venueName} · {displayAsset(to)}</p>
            ) : (
              <RailPicker balanceBelow
                value={landsOn}
                onChange={setLandsOn}
                rows={landRows}
                partySlug={partyExists ? partySlug : undefined}
                partyEntity={entity}
                allowedKinds={kindsForCategory(isFiat(to) ? 'fiat' : 'crypto')}
                loading={treasury.isFetching}
                readFailed={treasury.isError}
                placeholder={`Account for ${displayAsset(to)}`}
              />
            )}
          </Field>
        )}
      </div>

      {!chainSwap && (
        <>
          {/* THE VENUE'S FILLS, UP FRONT (his "I used to get relevant trade happened inside kucoin for conversion/swap", 1 Nov
              2026 — the old screen offered them; the rebuild had put them behind a tap): the days whose fills match the pair,
              one button each that presets the calculator, sit above the amounts. Before the To asset is picked, the venue's
              recent days say what they bought and offer their To in one tap. A pair with no fills of its own keeps the fold. */}
          {isExchange && !pairKnown && otherFills.some((x) => x.toCode) && (
            <Panel title={`${venueName}'s fills · ${fillsSpan}`} className="mb-1">
              <div className="space-y-2">
                {otherFills.map((x) => <OtherFill key={`${x.g.day}|${x.g.symbol}|${x.g.side}`} {...x} fromName={fromName} disabled={submit.isPending} onTo={setTo} />)}
              </div>
            </Panel>
          )}
          {isExchange && pairKnown && (fills.isSuccess && pairFills.length > 0 ? (
            <Panel title={`${venueName}'s fills · ${fillsSpan}`} className="mb-1">
              {fills.data.booked > 0 && <p className="mb-2 text-meta text-tg-hint">{fills.data.booked} already booked</p>}
              {/* A SMALL TABLE, NOT A BUTTON HOLDING A SENTENCE (1 Nov 2026 audit): one row per day — the day, what came in,
                  what went out, the rate and the fee in their own columns, "Use" at the right; the figures by rule 10. */}
              <div className={`${FILL_COLS} text-body`}>
                <div className={`${FILL_ROW} border-b border-stroke-soft pb-1 text-label font-medium uppercase tracking-wider text-tg-hint max-lg:hidden`}>
                  <span>Day</span><span className="text-right">In</span><span className="text-right">Out</span>
                  <span className="text-right max-lg:hidden">Rate</span><span />
                </div>
                {pairFills.map((p) => {
                  const { preset, over: dayOver, unbooked } = fillsCheck(p);
                  return (
                    <div key={`${p.g.day}|${p.g.symbol}|${p.g.side}`} className={`${FILL_ROW} space-y-1.5 border-b border-stroke-soft py-2 last:border-b-0 lg:[&>*]:col-span-full`}>
                      {/* From lg one table row; below it the day and Use on top, then each figure on its own labelled row
                          (a 390 px row cannot hold two amounts and a button — 1 Nov 2026 audit). */}
                      <div className={`${FILL_ROW} flex items-center justify-between gap-x-4`}>
                        <span className="min-w-0">
                          <span className="block whitespace-nowrap font-mono tabular text-meta">{fmtDay(p.g.first_at ?? p.g.day)}</span>
                          <span className="block text-meta text-tg-hint">{p.g.fills} fill{p.g.fills === 1 ? '' : 's'}</span>
                        </span>
                        <span className="text-right max-lg:hidden"><ReceiptAmount value={p.toAmount} asset={to} /></span>
                        <span className="text-right max-lg:hidden"><ReceiptAmount value={p.fromAmount} asset={from.asset} /></span>
                        <span className="whitespace-nowrap text-right font-mono tabular max-lg:hidden">{fillRate(p.g.rate)}</span>
                        <button type="button" className="btn-secondary min-h-[44px] w-full px-4 text-body lg:min-h-[40px] lg:px-3" disabled={submit.isPending}
                          onClick={() => applyFills(p, feeEntered)} aria-label={`Use the fills of ${fmtDay(p.g.first_at ?? p.g.day)}`}>Use</button>
                      </div>
                      {p.feeAsset && p.fee > 0 && (
                        <div className="flex items-baseline justify-between gap-3 text-body max-lg:hidden"><span className="text-meta text-tg-hint">Fee</span><ReceiptAmount value={p.fee} asset={p.feeAsset} /></div>
                      )}
                      <div className="space-y-0.5 lg:hidden">
                        {([['In', <ReceiptAmount key="i" value={p.toAmount} asset={to} />], ['Out', <ReceiptAmount key="o" value={p.fromAmount} asset={from.asset} />],
                          ['Rate', <span key="r" className="font-mono tabular">{fillRate(p.g.rate)}</span>],
                          ['Fee', p.feeAsset ? <ReceiptAmount key="f" value={p.fee} asset={p.feeAsset} /> : <span key="f" className="text-tg-hint">—</span>]] as const).map(([k, v]) => (
                          <div key={k} className="flex items-baseline justify-between gap-3 text-body"><span className="text-meta text-tg-hint">{k}</span>{v}</div>
                        ))}
                      </div>
                      {p.feeAsset && feeEntered && (
                        <p className="text-meta text-tg-hint">Figures before the fee — it is booked on its own.</p>
                      )}
                      {dayOver > 0 && (
                        unbooked ? (
                          <StateLine
                            tone="warn"
                            more={<>The books hold {fmtAmount(balance, from.asset)} {displayAsset(from.asset)} on {fromName}; the venue spent {fmtQuantity(preset, from.asset)} {displayAsset(from.asset)}.</>}
                            action={<Link to="/chain" className={TAP}>Book it from Chain →</Link>}
                          >
                            The money that moved in is not booked yet.
                          </StateLine>
                        ) : (
                          <StateLine
                            tone="warn"
                            more={<>{venueName} sold {fmtQuantity(dayOver, from.asset)} {displayAsset(from.asset)} more than {fromName} holds on the books ({fmtAmount(balance, from.asset)} {displayAsset(from.asset)}) — dust it already had. After the tap, trim the {displayAsset(from.asset)} box to {fmtAmount(balance, from.asset)}, or the post is refused.</>}
                          >
                            {fmtAmount(dayOver, from.asset)} {displayAsset(from.asset)} over the books — trim after the tap.
                          </StateLine>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          ) : (
            <Fold label="Venue fills">
              {fills.data && fills.data.booked > 0 && <p className="text-meta text-tg-hint">{fills.data.booked} already booked</p>}
              {fills.isPending && <Pending inline />}
              {fills.isError && <ReadFailed what="the fills" error={fills.error} onRetry={() => void fills.refetch()} />}
              {fills.isSuccess && <EmptyState>No fills for {displayAsset(from.asset)} → {displayAsset(to)}.</EmptyState>}
              {otherFills.map((x) => <OtherFill key={`${x.g.day}|${x.g.symbol}|${x.g.side}`} {...x} fromName={fromName} disabled={submit.isPending} onTo={setTo} />)}
            </Fold>
          ))}

          <div className="grid grid-cols-2 items-start gap-3 lg:gap-4">
            <Field label="From amount" hint={fromAuto ? 'auto' : undefined}>
              <input
                name="from_amount"
                className={`input${fromAuto ? ' text-tg-hint' : ''}`}
                value={fromText}
                onChange={(e) => math.setAmount(from.asset, e.target.value)}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{fromWords}</ErrorLine>
              {!fromWords && over > 0 && (
                <WarnLine>Over the balance by {fmtAmount(over, from.asset)}.</WarnLine>
              )}
            </Field>
            <Field label="To amount" hint={toAuto ? 'auto' : undefined}>
              <input
                name="to_amount"
                className={`input${toAuto ? ' text-tg-hint' : ''}`}
                value={toText}
                onChange={(e) => { if (to) math.setAmount(to, e.target.value); }}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{toWords}</ErrorLine>
            </Field>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Field label="Rate" hint={pairKnown ? `${math.rateWords}${math.rateIsDerived ? ' · auto' : ''}` : undefined}>
              <input
                name="rate"
                className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{rateProblemWords}</ErrorLine>
            </Field>
          </div>
          {bandSaid && (
            <StateLine tone="warn" more={bandSaid.rest || undefined}>
              {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
            </StateLine>
          )}

          <Fold label="More">
            <AsOfChips
              value={when} onChange={onWhenChange} disabled={submit.isPending}
              preset={(usedFills?.g.first_at && isoToDubaiWall(usedFills.g.first_at)) || undefined}
              presetLabel="From the fills"
            />
            {isExchange && pairKnown && (
              <Field label="Venue fee" hint="optional">
                <div className="flex gap-2">
                  <input className="input flex-1 min-w-0" value={venueFee} onChange={(e) => setVenueFee(e.target.value)} inputMode="decimal" placeholder="0" />
                  <select className="select w-28 shrink-0" value={feeAssetPicked} onChange={(e) => setVenueFeeAsset(e.target.value)}>
                    <option value={to}>{displayAsset(to)}</option>
                    <option value={from.asset}>{displayAsset(from.asset)}</option>
                  </select>
                </div>
                <ErrorLine>{feeWords}</ErrorLine>
              </Field>
            )}
            <Field label="Notes">
              <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
            </Field>
          </Fold>
        </>
      )}
    </FormFrame>
  );
}
